import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { format, isWithinInterval, parseISO, startOfDay, endOfDay } from "date-fns";
import {
  Eye,
  Edit,
  CheckCircle,
  XCircle,
  Clock,
  Search,
  FileText,
  User,
  Calendar as CalendarIcon,
  DollarSign,
  MapPin,
  Phone,
  Mail,
  Download,
  Filter,
  ChevronDown,
  X
} from "lucide-react";
import type { Database } from "@/integrations/supabase/types";
import type { DateRange } from "react-day-picker";

type ClaimStatus = Database["public"]["Enums"]["claim_status"];

interface Claim {
  id: string;
  claim_number: string;
  status: ClaimStatus;
  created_at: string;
  updated_at: string;
  loss_date: string;
  claim_type: string;
  policyholder_name: string;
  policyholder_email: string;
  policyholder_phone: string;
  policyholder_address: string;
  policyholder_city: string;
  policyholder_state: string;
  policyholder_zip: string;
  adjuster_name: string;
  adjuster_email: string;
  adjuster_phone: string;
  adjuster_company: string;
  adjuster_license: string;
  settlement_amount: number | null;
  settlement_date: string | null;
  family_dynamics: string | null;
  approved_terms: string | null;
  user_id: string;
  insurance_carrier_name: string | null;
}

const CLAIM_TYPES = [
  "Fire",
  "Water Damage",
  "Wind/Hail",
  "Theft",
  "Liability",
  "Vehicle",
  "Natural Disaster",
  "Other"
];

const AdminClaimsManagement = () => {
  const navigate = useNavigate();
  const [claims, setClaims] = useState<Claim[]>([]);
  const [companyMap, setCompanyMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<Claim>>({});
  const [saving, setSaving] = useState(false);

  // Filtering state
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [selectedClaimType, setSelectedClaimType] = useState<string>("all");
  const [filterOpen, setFilterOpen] = useState(false);

  // Bulk selection state
  const [selectedClaimIds, setSelectedClaimIds] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  useEffect(() => {
    fetchClaims();
  }, []);

  const fetchClaims = async () => {
    try {
      const { data, error } = await supabase
        .from("claims")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      const claimsData = data || [];
      setClaims(claimsData);

      // Fetch company from profiles for each unique submitter
      const userIds = Array.from(new Set(claimsData.map(c => c.user_id).filter(Boolean)));
      if (userIds.length > 0) {
        const { data: profilesData } = await supabase
          .from("profiles")
          .select("id, company")
          .in("id", userIds);
        const map: Record<string, string> = {};
        (profilesData || []).forEach((p: any) => {
          if (p.company) map[p.id] = p.company;
        });
        setCompanyMap(map);
      }
    } catch (error) {
      console.error("Error fetching placement:", error);
      toast.error("Failed to load placement");
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  const applyFilters = (claimsList: Claim[]) => {
    let filtered = claimsList;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(claim =>
        claim.claim_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        claim.policyholder_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        claim.adjuster_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Date range filter
    if (dateRange?.from) {
      filtered = filtered.filter(claim => {
        const claimDate = parseISO(claim.created_at);
        const from = startOfDay(dateRange.from!);
        const to = dateRange.to ? endOfDay(dateRange.to) : endOfDay(dateRange.from!);
        return isWithinInterval(claimDate, { start: from, end: to });
      });
    }

    // Claim type filter
    if (selectedClaimType && selectedClaimType !== "all") {
      filtered = filtered.filter(claim =>
        claim.claim_type.toLowerCase() === selectedClaimType.toLowerCase()
      );
    }

    return filtered;
  };

  const pendingClaims = claims.filter(c => c.status === "pending" || c.status === "in_review");
  const completedClaims = claims.filter(c => c.status === "approved" || c.status === "denied");

  const filteredPendingClaims = applyFilters(pendingClaims);
  const filteredCompletedClaims = applyFilters(completedClaims);

  const clearFilters = () => {
    setDateRange(undefined);
    setSelectedClaimType("all");
    setSearchTerm("");
  };

  const hasActiveFilters = dateRange?.from || (selectedClaimType && selectedClaimType !== "all");

  const handleViewClaim = (claim: Claim) => {
    setSelectedClaim(claim);
    setViewDialogOpen(true);
  };

  const handleEditClaim = (claim: Claim) => {
    setSelectedClaim(claim);
    setEditFormData({
      claim_number: claim.claim_number,
      claim_type: claim.claim_type,
      policyholder_name: claim.policyholder_name,
      policyholder_email: claim.policyholder_email,
      policyholder_phone: claim.policyholder_phone,
      policyholder_address: claim.policyholder_address,
      policyholder_city: claim.policyholder_city,
      policyholder_state: claim.policyholder_state,
      policyholder_zip: claim.policyholder_zip,
      adjuster_name: claim.adjuster_name,
      adjuster_email: claim.adjuster_email,
      adjuster_phone: claim.adjuster_phone,
      adjuster_company: claim.adjuster_company,
      settlement_amount: claim.settlement_amount,
      family_dynamics: claim.family_dynamics,
      approved_terms: claim.approved_terms,
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedClaim) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("claims")
        .update(editFormData)
        .eq("id", selectedClaim.id);

      if (error) throw error;

      toast.success("Placement updated successfully");
      setEditDialogOpen(false);
      fetchClaims();
    } catch (error) {
      console.error("Error updating placement:", error);
      toast.error("Failed to update placement");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (claimId: string, newStatus: ClaimStatus) => {
    try {
      const { error } = await supabase
        .from("claims")
        .update({ status: newStatus })
        .eq("id", claimId);

      if (error) throw error;

      // Send notification email
      try {
        await supabase.functions.invoke("send-status-change-email", {
          body: { claimId, newStatus },
        });
      } catch (emailError) {
        console.warn("Email notification failed:", emailError);
      }

      toast.success(`Placement ${newStatus === "approved" ? "approved" : newStatus === "denied" ? "denied" : "updated"} successfully`);
      fetchClaims();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update placement status");
    }
  };

  // Bulk actions
  const handleSelectAll = (claimsList: Claim[], checked: boolean) => {
    if (checked) {
      const pendingIds = claimsList
        .filter(c => c.status === "pending" || c.status === "in_review")
        .map(c => c.id);
      setSelectedClaimIds(new Set(pendingIds));
    } else {
      setSelectedClaimIds(new Set());
    }
  };

  const handleSelectClaim = (claimId: string, checked: boolean) => {
    const newSelected = new Set(selectedClaimIds);
    if (checked) {
      newSelected.add(claimId);
    } else {
      newSelected.delete(claimId);
    }
    setSelectedClaimIds(newSelected);
  };

  const handleBulkAction = async (action: "approved" | "denied") => {
    if (selectedClaimIds.size === 0) {
      toast.error("No placement selected");
      return;
    }

    setBulkActionLoading(true);
    try {
      const claimIds = Array.from(selectedClaimIds);

      const { error } = await supabase
        .from("claims")
        .update({ status: action })
        .in("id", claimIds);

      if (error) throw error;

      // Send notification emails for each claim
      for (const claimId of claimIds) {
        try {
          await supabase.functions.invoke("send-status-change-email", {
            body: { claimId, newStatus: action },
          });
        } catch (emailError) {
          console.warn(`Email notification failed for claim ${claimId}:`, emailError);
        }
      }

      toast.success(`${claimIds.length} placement ${action === "approved" ? "approved" : "denied"} successfully`);
      setSelectedClaimIds(new Set());
      fetchClaims();
    } catch (error) {
      console.error("Error performing bulk action:", error);
      toast.error("Failed to update placement");
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Export functions
  const exportToCSV = (claimsList: Claim[]) => {
    const headers = [
      "Placement Number",
      "Company",
      "Status",
      "Type",
      "Policyholder Name",
      "Policyholder Email",
      "Policyholder Phone",
      "Address",
      "City",
      "State",
      "ZIP",
      "Adjuster Name",
      "Adjuster Company",
      "Adjuster Email",
      "Adjuster Phone",
      "Loss Date",
      "Settlement Amount",
      "Created At"
    ];

    const rows = claimsList.map(claim => [
      claim.claim_number,
      companyMap[claim.user_id] || "",
      claim.status,
      claim.claim_type,
      claim.policyholder_name,
      claim.policyholder_email,
      claim.policyholder_phone,
      claim.policyholder_address,
      claim.policyholder_city,
      claim.policyholder_state,
      claim.policyholder_zip,
      claim.adjuster_name,
      claim.adjuster_company,
      claim.adjuster_email,
      claim.adjuster_phone,
      claim.loss_date,
      claim.settlement_amount?.toString() || "",
      format(new Date(claim.created_at), "yyyy-MM-dd HH:mm:ss")
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `placement-export-${format(new Date(), "yyyy-MM-dd")}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Placement exported to CSV");
  };

  const exportToPDF = (claimsList: Claim[]) => {
    // Create a printable HTML document
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Placement Report - ${format(new Date(), "MMMM d, yyyy")}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #333; border-bottom: 2px solid #448bcd; padding-bottom: 10px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 11px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #448bcd; color: white; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .status-approved { color: green; font-weight: bold; }
          .status-denied { color: red; font-weight: bold; }
          .status-pending { color: orange; font-weight: bold; }
          .status-in_review { color: blue; font-weight: bold; }
          .footer { margin-top: 30px; text-align: center; color: #666; font-size: 12px; }
        </style>
      </head>
      <body>
        <h1>Placement Report</h1>
        <p>Generated on ${format(new Date(), "MMMM d, yyyy 'at' h:mm a")}</p>
        <p>Total Placement: ${claimsList.length}</p>
        <table>
          <thead>
            <tr>
              <th>Placement #</th>
              <th>Company</th>
              <th>Status</th>
              <th>Type</th>
              <th>Policyholder</th>
              <th>Adjuster</th>
              <th>Loss Date</th>
              <th>Settlement</th>
            </tr>
          </thead>
          <tbody>
            ${claimsList.map(claim => `
              <tr>
                <td>${claim.claim_number}</td>
                <td>${companyMap[claim.user_id] || '-'}</td>
                <td class="status-${claim.status}">${claim.status.replace("_", " ").toUpperCase()}</td>
                <td>${claim.claim_type}</td>
                <td>${claim.policyholder_name}<br/><small>${claim.policyholder_email}</small></td>
                <td>${claim.adjuster_name}<br/><small>${claim.adjuster_company}</small></td>
                <td>${format(new Date(claim.loss_date), "MMM d, yyyy")}</td>
                <td>${claim.settlement_amount ? `$${claim.settlement_amount.toLocaleString()}` : "-"}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>
        <div class="footer">
          <p>TPS Placement Management System</p>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
      toast.success("PDF generated - print dialog opened");
    }
  };

  const getStatusLabel = (status: ClaimStatus) => {
    if (status === "pending") return "Pending";
    if (status === "denied") return "Closed";
    // in_review or approved
    return "Active";
  };

  const getStatusBadge = (status: ClaimStatus) => {
    const variants: Record<ClaimStatus, { variant: "default" | "secondary" | "destructive" | "outline"; icon: React.ReactNode }> = {
      pending: { variant: "secondary", icon: <Clock className="h-3 w-3 mr-1" /> },
      in_review: { variant: "outline", icon: <Eye className="h-3 w-3 mr-1" /> },
      approved: { variant: "default", icon: <CheckCircle className="h-3 w-3 mr-1" /> },
      denied: { variant: "destructive", icon: <XCircle className="h-3 w-3 mr-1" /> },
    };

    const { variant, icon } = variants[status];
    return (
      <Badge variant={variant} className="flex items-center w-fit">
        {icon}
        {getStatusLabel(status)}
      </Badge>
    );
  };

  const ClaimsTable = ({ claimsList, showActions = true, showCheckboxes = false }: { claimsList: Claim[]; showActions?: boolean; showCheckboxes?: boolean }) => {
    const selectableClaims = claimsList.filter(c => c.status === "pending" || c.status === "in_review");
    const allSelected = selectableClaims.length > 0 && selectableClaims.every(c => selectedClaimIds.has(c.id));

    return (
      <Table>
        <TableHeader>
          <TableRow>
            {showCheckboxes && (
              <TableHead className="w-12">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={(checked) => handleSelectAll(claimsList, !!checked)}
                />
              </TableHead>
            )}
            <TableHead>Placement #</TableHead>
            <TableHead>Company</TableHead>
            <TableHead>Submitted By</TableHead>
            <TableHead>Policyholder</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>Adjuster</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {claimsList.length === 0 ? (
            <TableRow>
              <TableCell colSpan={showCheckboxes ? 10 : 9} className="text-center text-muted-foreground py-8">
                No placement found
              </TableCell>
            </TableRow>
          ) : (
            claimsList.map((claim) => (
              <TableRow key={claim.id}>
                {showCheckboxes && (
                  <TableCell>
                    {(claim.status === "pending" || claim.status === "in_review") && (
                      <Checkbox
                        checked={selectedClaimIds.has(claim.id)}
                        onCheckedChange={(checked) => handleSelectClaim(claim.id, !!checked)}
                      />
                    )}
                  </TableCell>
                )}
                <TableCell className="font-medium">{claim.claim_number}</TableCell>
                <TableCell>{companyMap[claim.user_id] || '-'}</TableCell>
                <TableCell>{claim.adjuster_name || '-'}</TableCell>
                <TableCell>{claim.policyholder_name}</TableCell>
                <TableCell>{claim.claim_type}</TableCell>
                <TableCell>{getStatusBadge(claim.status)}</TableCell>
                <TableCell>{format(new Date(claim.created_at), "MMM d, yyyy")}</TableCell>
                <TableCell>{claim.adjuster_name}<br /><span className="text-xs text-muted-foreground">{claim.adjuster_email}</span></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewClaim(claim)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {showActions && (claim.status === "pending" || claim.status === "in_review") && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditClaim(claim)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleStatusChange(claim.id, "approved")}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleStatusChange(claim.id, "denied")}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Deny
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Placement Management</h1>
          <p className="text-muted-foreground">Review, edit, and manage placements</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search placement..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Filter Popover */}
          <Popover open={filterOpen} onOpenChange={setFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Filter className="h-4 w-4" />
                Filters
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center">
                    !
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="end">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Filters</h4>
                  {hasActiveFilters && (
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      <X className="h-4 w-4 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>

                {/* Date Range */}
                <div className="space-y-2">
                  <Label>Date Range</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateRange?.from ? (
                          dateRange.to ? (
                            <>
                              {format(dateRange.from, "LLL dd, y")} - {format(dateRange.to, "LLL dd, y")}
                            </>
                          ) : (
                            format(dateRange.from, "LLL dd, y")
                          )
                        ) : (
                          <span>Pick a date range</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        initialFocus
                        mode="range"
                        defaultMonth={dateRange?.from}
                        selected={dateRange}
                        onSelect={setDateRange}
                        numberOfMonths={2}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Placement Type */}
                <div className="space-y-2">
                  <Label>Placement Type</Label>
                  <Select value={selectedClaimType} onValueChange={setSelectedClaimType}>
                    <SelectTrigger>
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {CLAIM_TYPES.map(type => (
                        <SelectItem key={type} value={type.toLowerCase()}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button className="w-full" onClick={() => setFilterOpen(false)}>
                  Apply Filters
                </Button>
              </div>
            </PopoverContent>
          </Popover>

          {/* Export Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Download className="h-4 w-4" />
                Export
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportToCSV(applyFilters(claims))}>
                <FileText className="h-4 w-4 mr-2" />
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportToPDF(applyFilters(claims))}>
                <FileText className="h-4 w-4 mr-2" />
                Export as PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground">Active filters:</span>
          {dateRange?.from && (
            <Badge variant="secondary" className="gap-1">
              {format(dateRange.from, "MMM d")} - {dateRange.to ? format(dateRange.to, "MMM d, yyyy") : format(dateRange.from, "yyyy")}
              <button onClick={() => setDateRange(undefined)} className="ml-1 hover:text-destructive">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {selectedClaimType && selectedClaimType !== "all" && (
            <Badge variant="secondary" className="gap-1">
              {selectedClaimType}
              <button onClick={() => setSelectedClaimType("all")} className="ml-1 hover:text-destructive">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Placement</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{claims.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{claims.filter(c => c.status === "pending").length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{claims.filter(c => c.status === "approved").length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Closed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{claims.filter(c => c.status === "denied").length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Placement Tabs */}
      <Card>
        <CardContent className="pt-6">
          <Tabs defaultValue="pending">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <TabsList>
                <TabsTrigger value="pending" className="gap-2">
                  <Clock className="h-4 w-4" />
                  Pending ({filteredPendingClaims.length})
                </TabsTrigger>
                <TabsTrigger value="completed" className="gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Completed ({filteredCompletedClaims.length})
                </TabsTrigger>
              </TabsList>

              {/* Bulk Actions */}
              {selectedClaimIds.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {selectedClaimIds.size} selected
                  </span>
                  <Button
                    size="sm"
                    onClick={() => handleBulkAction("approved")}
                    disabled={bulkActionLoading}
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Approve All
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleBulkAction("denied")}
                    disabled={bulkActionLoading}
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Deny All
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedClaimIds(new Set())}
                  >
                    Clear
                  </Button>
                </div>
              )}
            </div>

            <TabsContent value="pending">
              <ClaimsTable claimsList={filteredPendingClaims} showActions={true} showCheckboxes={true} />
            </TabsContent>
            <TabsContent value="completed">
              <ClaimsTable claimsList={filteredCompletedClaims} showActions={false} showCheckboxes={false} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* View Placement Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Placement Details - {selectedClaim?.claim_number}
            </DialogTitle>
            <DialogDescription>
              Full placement information and status
            </DialogDescription>
          </DialogHeader>

          {selectedClaim && (
            <div className="grid gap-6">
              {/* Status */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Current Status</span>
                {getStatusBadge(selectedClaim.status)}
              </div>

              {/* Policyholder Info */}
              <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
                <h4 className="font-semibold flex items-center gap-2 text-primary border-b pb-2">
                  <User className="h-4 w-4" /> Policyholder Information
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Name:</span>
                    <p className="font-medium">{selectedClaim.policyholder_name}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" /> Email:</span>
                    <p className="font-medium">{selectedClaim.policyholder_email}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground flex items-center gap-1"><Phone className="h-3 w-3" /> Phone:</span>
                    <p className="font-medium">{selectedClaim.policyholder_phone}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> Address:</span>
                    <p className="font-medium">
                      {selectedClaim.policyholder_address}, {selectedClaim.policyholder_city}, {selectedClaim.policyholder_state} {selectedClaim.policyholder_zip}
                    </p>
                  </div>
                  {selectedClaim.family_dynamics?.match(/Property:\s*([^,]+)/)?.[1] && (
                    <div>
                      <span className="text-muted-foreground">Property:</span>
                      <p className="font-medium">{selectedClaim.family_dynamics.match(/Property:\s*([^,]+)/)?.[1]}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Placement Details */}
              <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
                <h4 className="font-semibold flex items-center gap-2 text-primary border-b pb-2">
                  <FileText className="h-4 w-4" /> Placement Details
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Placement Type:</span>
                    <p className="font-medium">{selectedClaim.claim_type}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground flex items-center gap-1"><CalendarIcon className="h-3 w-3" /> Loss Date:</span>
                    <p className="font-medium">{format(new Date(selectedClaim.loss_date), "MMMM d, yyyy")}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground flex items-center gap-1"><DollarSign className="h-3 w-3" /> ALE Limit:</span>
                    <p className="font-medium">
                      {selectedClaim.settlement_amount
                        ? `$${selectedClaim.settlement_amount.toLocaleString()}`
                        : "Not set"}
                    </p>
                  </div>
                  {selectedClaim.approved_terms?.match(/n:\s*([\d.]+)/)?.[1] && (
                    <div>
                      <span className="text-muted-foreground flex items-center gap-1"><DollarSign className="h-3 w-3" /> ALE Advanced:</span>
                      <p className="font-medium">
                        ${selectedClaim.approved_terms.match(/n:\s*([\d.]+)/)?.[1]}
                      </p>
                    </div>
                  )}
                  {selectedClaim.approved_terms?.match(/Policy Type:\s*([^,]+)/)?.[1] && (
                    <div>
                      <span className="text-muted-foreground">Policy Type:</span>
                      <p className="font-medium">{selectedClaim.approved_terms.match(/Policy Type:\s*([^,]+)/)?.[1].trim()}</p>
                    </div>
                  )}
                  {selectedClaim.approved_terms?.match(/Deductible Required:\s*(\w+)/)?.[1] && (
                    <div>
                      <span className="text-muted-foreground">Deductible Required:</span>
                      <p className="font-medium">{selectedClaim.approved_terms.match(/Deductible Required:\s*(\w+)/)?.[1]}</p>
                    </div>
                  )}
                  {selectedClaim.approved_terms?.match(/Deductible Amount:\s*\$?([\d.]+)/)?.[1] && (
                    <div>
                      <span className="text-muted-foreground flex items-center gap-1"><DollarSign className="h-3 w-3" /> Deductible Amount:</span>
                      <p className="font-medium">${selectedClaim.approved_terms.match(/Deductible Amount:\s*\$?([\d.]+)/)?.[1]}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Created:</span>
                    <p className="font-medium">{format(new Date(selectedClaim.created_at), "MMMM d, yyyy 'at' h:mm a")}</p>
                  </div>
                </div>
              </div>

              {/* Family Dynamics */}
              {selectedClaim.family_dynamics && (
                <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
                  <h4 className="font-semibold flex items-center gap-2 text-primary border-b pb-2">
                    <User className="h-4 w-4" /> Family Dynamics
                  </h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    {selectedClaim.family_dynamics.match(/Adults:\s*(\d+)/)?.[1] && (
                      <div>
                        <span className="text-muted-foreground">Adults:</span>
                        <p className="font-medium">{selectedClaim.family_dynamics.match(/Adults:\s*(\d+)/)?.[1]}</p>
                      </div>
                    )}
                    {selectedClaim.family_dynamics.match(/Children:\s*(\d+)/)?.[1] && (
                      <div>
                        <span className="text-muted-foreground">Children:</span>
                        <p className="font-medium">{selectedClaim.family_dynamics.match(/Children:\s*(\d+)/)?.[1]}</p>
                      </div>
                    )}
                    {selectedClaim.family_dynamics.match(/Pets:\s*(\w+)/)?.[1] && (
                      <div>
                        <span className="text-muted-foreground">Pets:</span>
                        <p className="font-medium">{selectedClaim.family_dynamics.match(/Pets:\s*(\w+)/)?.[1]}</p>
                      </div>
                    )}
                  </div>
                  {(() => {
                    const remaining = selectedClaim.family_dynamics
                      .replace(/Adults:\s*\d+,\s*/, "")
                      .replace(/Children:\s*\d+,\s*/, "")
                      .replace(/Pets:\s*\w+,\s*/, "")
                      .replace(/,?\s*Property:[^,]+/i, "")
                      .replace(/^,\s*|\s*,$/g, "")
                      .trim();
                    return remaining ? <p className="text-sm text-muted-foreground">{remaining}</p> : null;
                  })()}
                </div>
              )}

              {/* Approved Terms */}
              {selectedClaim.approved_terms && (
                <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
                  <h4 className="font-semibold flex items-center gap-2 text-primary border-b pb-2">
                    <FileText className="h-4 w-4" /> Approved Terms
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {selectedClaim.approved_terms.match(/Lease:\s*([^,]+)/)?.[1] && (
                      <div>
                        <span className="text-muted-foreground">Lease:</span>
                        <p className="font-medium">{selectedClaim.approved_terms.match(/Lease:\s*([^,]+)/)?.[1].trim()}</p>
                      </div>
                    )}
                    {selectedClaim.approved_terms.match(/Hotel Approved To:\s*([^,]+)/)?.[1] && (
                      <div>
                        <span className="text-muted-foreground">Hotel Approved To:</span>
                        <p className="font-medium">{selectedClaim.approved_terms.match(/Hotel Approved To:\s*([^,]+)/)?.[1].trim()}</p>
                      </div>
                    )}
                    {selectedClaim.approved_terms.match(/Hotel Rooms:\s*(\d+)/)?.[1] && (
                      <div>
                        <span className="text-muted-foreground">Hotel Rooms:</span>
                        <p className="font-medium">{selectedClaim.approved_terms.match(/Hotel Rooms:\s*(\d+)/)?.[1]}</p>
                      </div>
                    )}
                    {selectedClaim.approved_terms.match(/Policy Time Limit:\s*([^,]+)/)?.[1] && (
                      <div>
                        <span className="text-muted-foreground">Policy Time Limit:</span>
                        <p className="font-medium">{selectedClaim.approved_terms.match(/Policy Time Limit:\s*([^,]+)/)?.[1].trim()}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Adjuster Info */}
              <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
                <h4 className="font-semibold flex items-center gap-2 text-primary border-b pb-2">
                  <User className="h-4 w-4" /> Adjuster Information
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Name:</span>
                    <p className="font-medium">{selectedClaim.adjuster_name}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Company:</span>
                    <p className="font-medium">{selectedClaim.adjuster_company}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Email:</span>
                    <p className="font-medium">{selectedClaim.adjuster_email}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Phone:</span>
                    <p className="font-medium">{selectedClaim.adjuster_phone}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
            {selectedClaim && (selectedClaim.status === "pending" || selectedClaim.status === "in_review") && (
              <>
                <Button
                  variant="default"
                  onClick={() => {
                    handleStatusChange(selectedClaim.id, "approved");
                    setViewDialogOpen(false);
                  }}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    handleStatusChange(selectedClaim.id, "denied");
                    setViewDialogOpen(false);
                  }}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Deny
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Placement Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Placement - {selectedClaim?.claim_number}</DialogTitle>
            <DialogDescription>
              Make changes to the placement details
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Placement Number</Label>
              <Input
                value={editFormData.claim_number || ""}
                onChange={(e) => setEditFormData({ ...editFormData, claim_number: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Placement Type</Label>
                <Input
                  value={editFormData.claim_type || ""}
                  onChange={(e) => setEditFormData({ ...editFormData, claim_type: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Policyholder Name</Label>
              <Input
                value={editFormData.policyholder_name || ""}
                onChange={(e) => setEditFormData({ ...editFormData, policyholder_name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Policyholder Email</Label>
                <Input
                  type="email"
                  value={editFormData.policyholder_email || ""}
                  onChange={(e) => setEditFormData({ ...editFormData, policyholder_email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Policyholder Phone</Label>
                <Input
                  value={editFormData.policyholder_phone || ""}
                  onChange={(e) => setEditFormData({ ...editFormData, policyholder_phone: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={editFormData.policyholder_address || ""}
                onChange={(e) => setEditFormData({ ...editFormData, policyholder_address: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>City</Label>
                <Input
                  value={editFormData.policyholder_city || ""}
                  onChange={(e) => setEditFormData({ ...editFormData, policyholder_city: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Input
                  value={editFormData.policyholder_state || ""}
                  onChange={(e) => setEditFormData({ ...editFormData, policyholder_state: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>ZIP</Label>
                <Input
                  value={editFormData.policyholder_zip || ""}
                  onChange={(e) => setEditFormData({ ...editFormData, policyholder_zip: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Adjuster Name</Label>
                <Input
                  value={editFormData.adjuster_name || ""}
                  onChange={(e) => setEditFormData({ ...editFormData, adjuster_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Adjuster Company</Label>
                <Input
                  value={editFormData.adjuster_company || ""}
                  onChange={(e) => setEditFormData({ ...editFormData, adjuster_company: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Adjuster Email</Label>
                <Input
                  type="email"
                  value={editFormData.adjuster_email || ""}
                  onChange={(e) => setEditFormData({ ...editFormData, adjuster_email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Adjuster Phone</Label>
                <Input
                  value={editFormData.adjuster_phone || ""}
                  onChange={(e) => setEditFormData({ ...editFormData, adjuster_phone: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Settlement Amount ($)</Label>
              <Input
                type="number"
                value={editFormData.settlement_amount || ""}
                onChange={(e) => setEditFormData({ ...editFormData, settlement_amount: e.target.value ? parseFloat(e.target.value) : null })}
              />
            </div>

            <div className="space-y-2">
              <Label>Family Dynamics</Label>
              <Textarea
                value={editFormData.family_dynamics || ""}
                onChange={(e) => setEditFormData({ ...editFormData, family_dynamics: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Approved Terms</Label>
              <Textarea
                value={editFormData.approved_terms || ""}
                onChange={(e) => setEditFormData({ ...editFormData, approved_terms: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminClaimsManagement;
